import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';
import validate from 'validate.js';

import {
  AdornedInput,
  Box,
  DateInput,
  DatePicker,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  LabeledControl,
  Modal,
  Switch,
} from '@zillow/constellation-10';

import { useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_createComplete } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { usePropertyEditMutation } from '../../../hooks/usePropertyEditMutation';
import useRentZestimate from '../../../hooks/useRentZestimate';
import accessUtils from '../../../utils/accessUtils';
import { postingConstraints } from '../../../utils/formValidation';

import InlineStaticAlert from '../../Alerts/InlineStaticAlert';
import ZestimateHelper from '../../PostingPath/ZestimateHelper';
import ModalFooter from '../ModalFooter';

import type { ChangeEvent, KeyboardEvent } from 'react';

const emptyUnit = {
  name: '',
  squareFeet: '',
  dateAvailable: '',
  price: '',
  active: true,
};

type Props = {
  floorPlanIndex: number;
  onClose: () => void;
  onSuccess?: (unitIndex: number) => void;
};

const CreateUnitModal = ({ floorPlanIndex, onClose, onSuccess }: Props) => {
  const router = useRouter();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails, refetch: refetchListingDetails } = useGetListingDetails(listingAlias);

  const floorplans = listingDetails?.floorPlans || [];
  const floorPlan = floorplans[floorPlanIndex];
  const hasAnalyticsRentComps = listingDetails?.hasAnalyticsRentComps ?? false;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;

  const [unit, setUnit] = useState(emptyUnit);
  const [unitErrors, setUnitErrors] = useState<Record<string, string>>({});
  const [genericError, setGenericError] = useState('');
  const [dateAvailable, setDateAvailable] = useState('');
  const startOfYesterday = new Date(new Date(new Date()).getTime() - 24 * 3600 * 1000);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });

  useEffect(() => {
    const newDateAvailable = new Date(dateAvailable);

    if (isValid(newDateAvailable)) {
      setUnit({ ...unit, dateAvailable: format(newDateAvailable, 'yyyy-MM-dd') });
    } else {
      setUnit({ ...unit, dateAvailable: '' });
    }
  }, [dateAvailable]);

  const rentZestimate = useRentZestimate({
    hasAnalyticsRentComps,
    street: listingDetails?.street || '',
    city: listingDetails?.city || '',
    state: listingDetails?.state || '',
    zip: listingDetails?.zip || '',
    unitName: unit?.name,
  });

  const { mutateAsync: createUnit, isPending: isSubmitting } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyFloorPlanUnitWebApiHandler_createComplete,
    successToastMessage: 'Unit created',
    skipToast: true,
    meta: {
      invalidates: ['mf-costsAndFees'],
      context: { api: 'RentalManagerUserPropertyFloorPlanUnitWebApiHandler_createComplete', details: 'create unit' },
    },
  });

  const handleSubmit = async () => {
    const floorPlanId = floorPlan?.id;

    if (!floorPlanId) {
      setGenericError('Something went wrong, refresh the page and try again');
      return;
    }

    try {
      const response = await createUnit({
        postData: {
          floorPlanId,
          name: unit.name,
          price: unit.price,
          squareFeet: unit.squareFeet ? Number(unit.squareFeet) : undefined,
          dateAvailable: unit.dateAvailable || undefined,
          active: unit.active,
        },
      });

      onClose();

      // Refetch to get fresh data with the new unit, then call onSuccess with its index
      if (onSuccess && response?.id !== undefined) {
        const { data: freshListingDetails } = await refetchListingDetails();
        const freshFloorPlan = freshListingDetails?.floorPlans?.[floorPlanIndex];
        const unitIndex = freshFloorPlan?.units?.findIndex((u) => u.id === response.id) ?? -1;
        if (unitIndex !== -1) {
          onSuccess(unitIndex);
        }
      }
    } catch (error) {
      const err = error as {
        message?: string;
        errorDetails?: {
          message?: string;
          errorByFieldName?: Record<string, string>;
        };
      };

      if (err.errorDetails && err.errorDetails.errorByFieldName) {
        setUnitErrors(err.errorDetails.errorByFieldName);
      } else {
        setGenericError(
          err.errorDetails?.message || err.message || 'Something went wrong, refresh the page and try again',
        );
      }
    }
  };

  const handleUnitChange = (key: string, value: string | boolean) => {
    setGenericError('');

    if (key === 'squareFeet' && String(value).includes('.')) {
      setUnitErrors({ ...unitErrors, [key]: 'Enter only whole numbers.' });
    } else if (unitErrors[key]) {
      setUnitErrors({ ...unitErrors, [key]: '' });
    }

    setUnit({
      ...unit,
      [key]: value,
    });
  };

  const handleUnitNameOnBlur = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const unitNameError = validate({ unitName: event.target.value }, { unitName: postingConstraints.unitName })
        ?.unitName?.[0];

      if (unitNameError) {
        setUnitErrors({ ...unitErrors, name: unitNameError });
      }
    },
    [unitErrors],
  );

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Space') {
      (event.target as HTMLInputElement).click();
    }
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', lg: 'md' }}
      header={<Heading level={2}>Add unit</Heading>}
      body={
        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box css={{ flex: '1 1 50%' }}>
              <FormField
                error={!!unitErrors.name}
                label={<Label required={true}>Unit number</Label>}
                control={
                  <AdornedInput
                    data-testid="unit-number"
                    startAdornment={<AdornedInput.Adornment>#</AdornedInput.Adornment>}
                    input={
                      <AdornedInput.Input
                        name="name"
                        required={true}
                        value={unit?.name}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => handleUnitChange('name', e.target.value)}
                        onBlur={handleUnitNameOnBlur}
                      />
                    }
                  />
                }
                description={unitErrors.name && <FormHelp>{unitErrors.name}</FormHelp>}
              />
            </Box>

            <Box css={{ flex: '1 1 50%' }}>
              <FormField
                error={!!unitErrors.price}
                label={<Label required={true}>{canAccessTotalMonthlyPriceWithFee ? 'Base rent' : 'Price'}</Label>}
                control={
                  <>
                    <AdornedInput
                      data-testid="price"
                      input={
                        <AdornedInput.Input
                          name="price"
                          value={unit?.price}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => handleUnitChange('price', e.target.value)}
                        />
                      }
                      startAdornment={<AdornedInput.Adornment>$</AdornedInput.Adornment>}
                      endAdornment={<AdornedInput.Adornment>/mo</AdornedInput.Adornment>}
                    />
                    {hasAnalyticsRentComps && <ZestimateHelper rentZestimate={rentZestimate} />}
                  </>
                }
                description={unitErrors.price && <FormHelp>{unitErrors.price}</FormHelp>}
              />
            </Box>
          </Box>

          <InlineStaticAlert />

          <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
            <Box css={{ flex: '1 1 50%' }}>
              <FormField
                error={!!unitErrors.dateAvailable}
                optional={canAccessTotalMonthlyPriceWithFee}
                label={<Label>Date available</Label>}
                control={
                  <DatePicker
                    input={<DateInput data-testid="date-available" />}
                    minDate={startOfYesterday}
                    value={dateAvailable}
                    onValueChange={(newValue: string | Date) =>
                      setDateAvailable(typeof newValue === 'string' ? newValue : format(newValue, 'yyyy-MM-dd'))
                    }
                  />
                }
                description={unitErrors.dateAvailable && <FormHelp>{unitErrors.dateAvailable}</FormHelp>}
              />
            </Box>

            <Box css={{ flex: '1 1 50%' }}>
              <FormField
                error={!!unitErrors.squareFeet}
                optional={canAccessTotalMonthlyPriceWithFee}
                label={<Label>Square footage</Label>}
                control={
                  <Input
                    data-testid="square-feet"
                    name="squareFeet"
                    value={unit?.squareFeet}
                    type="number"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleUnitChange('squareFeet', e.target.value)}
                  />
                }
                description={unitErrors.squareFeet && <FormHelp>{unitErrors.squareFeet}</FormHelp>}
              />
            </Box>
          </Box>

          <Box>
            <div className="CreateUnitModal-switch-container">
              <LabeledControl
                aria-label="Unit available"
                tabIndex={0}
                size="lg"
                controlId="Unit-available"
                label={<Label>Unit available</Label>}
                labelPosition="left"
                control={
                  <Switch
                    data-testid="CreateUnitModal-switch"
                    checked={unit?.active}
                    onChange={() => handleUnitChange('active', !unit?.active)}
                    onKeyUp={(event: KeyboardEvent<HTMLInputElement>) => handleKeyUp(event)}
                  />
                }
              />

              {unitErrors.active ? (
                <FormHelp error={true}>{unitErrors.active}</FormHelp>
              ) : (
                <Box css={{ minHeight: '400' }} />
              )}
            </div>
          </Box>
        </Box>
      }
      footer={
        <ModalFooter
          submitText="Save"
          onSubmit={handleSubmit}
          onCancel={onClose}
          errorMessage={genericError}
          hasError={!!genericError}
          isSubmitting={isSubmitting}
          isSubmitDisabled={!unit?.name || !unit?.price || !!unitErrors.squareFeet || isSubmitting}
        />
      }
    />
  );
};

export { CreateUnitModal };
export default CreateUnitModal;
